import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@hero/web-components';
import { StatusBadgeConfigType } from '@hero/web-components';
import { useOrderDisplayInfo, UseOrderAgainConfig, useOrderDrawerStore, useOrderActions } from '@hero/hooks-shared';
import { isWaybillGenerationStale } from '@hero/core';
import { useOrderApi } from '@hero/api-hooks';
import { useFeature } from '@hero/web-context';
import { useViewerLeg } from '@hero/web-hooks';
import type { Order } from '@hero/schema';
import { StackedDrawer } from '../../../app/Drawer/StackedDrawer';
import { OrderDrawerHeader } from './OrderDrawerHeader';
import { OrderSummaryCard } from './OrderSummaryCard';
import { OrderTimeline } from '../Timeline';
import { OrderDetailsTab } from './OrderDetailsTab';
import { OrderPhotosTab } from './OrderPhotosTab';
import { OrderActions } from '../OrderActions';
import { GovernmentWaybillRef } from '../../Apps/GovernmentWaybillRef';

type CustomerOrderActionsComponent = React.ComponentType<{
  order: Order;
  displayForUser?: 'customer' | 'carrier';
  className?: string;
}>;

export interface OrderDrawerProps {
  displayForUser: 'customer' | 'carrier';
  orderAgainConfig?: UseOrderAgainConfig;
  statusConfig?: StatusBadgeConfigType;
  CustomerOrderActions?: CustomerOrderActionsComponent;
  syncUrl?: boolean | string;
}

interface OrderDrawerContentProps {
  order: Order;
  displayForUser: 'customer' | 'carrier';
  orderAgainConfig?: UseOrderAgainConfig;
  statusConfig?: StatusBadgeConfigType;
  CustomerOrderActions?: CustomerOrderActionsComponent;
}

const OrderDrawerContent: React.FC<OrderDrawerContentProps> = ({
  order,
  displayForUser,
  orderAgainConfig,
  statusConfig,
  CustomerOrderActions,
}) => {
  const { t } = useTranslation('m-order');
  const queryClient = useQueryClient();
  // Photos upload is a carrier action — show Add in the empty state + gallery.
  const canAddPhotos = displayForUser === 'carrier';
  const isWaybillGenerating =
    order.waybill?.status === 'generating' && !isWaybillGenerationStale(order);
  const { isEnabled: canEditOrder } = useFeature('order_module', 'sub_edit_order');
  const { isCancelable } = useOrderActions(order, { displayForUser });
  const canManagePost = displayForUser === 'carrier' && canEditOrder && isCancelable;
  const [isChangeClientOpen, setIsChangeClientOpen] = useState(false);

  // Poll the order while a waybill PDF is being generated so the drawer
  // flips from "Generating…" → Download without a manual refresh.
  useEffect(() => {
    if (!isWaybillGenerating) return;
    const id = window.setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: ['order'] });
    }, 2500);
    return () => window.clearInterval(id);
  }, [isWaybillGenerating, order._id, queryClient]);

  const viewerLeg = useViewerLeg(order);
  const { orderInfo, pricing, status, timeline, visibility, editability, priceUi } =
    useOrderDisplayInfo({
      order,
      displayForUser,
      viewerLeg,
    });

  const scrollableClasses = `w-full flex-1 mt-6 overflow-y-auto [scrollbar-gutter:stable]
    [&::-webkit-scrollbar]:w-2
    [&::-webkit-scrollbar-track]:bg-gray-100
    [&::-webkit-scrollbar-track]:rounded-full
    [&::-webkit-scrollbar-thumb]:bg-gray-300
    [&::-webkit-scrollbar-thumb]:rounded-full
    [&::-webkit-scrollbar-thumb]:hover:bg-gray-400
    [&::-webkit-scrollbar-thumb]:transition-colors`;

  return (
    <div className="flex flex-col h-full">
      <OrderDrawerHeader
        pickupCity={orderInfo.pickup?.address?.city?.name}
        dropoffCity={orderInfo.dropoff?.address?.city?.name}
        refMain={orderInfo.ref?.main}
        refShort={orderInfo.ref?.short}
        secondaryRef={<GovernmentWaybillRef order={order} />}
      />

      <Tabs defaultValue="summary" className="w-full flex-1 min-h-0 flex flex-col">
        <TabsList className="w-full flex flex-shrink-0 flex-row">
          <TabsTrigger value="summary">{t('drawer.tabs.summary')}</TabsTrigger>
          <TabsTrigger value="details">{t('drawer.tabs.details')}</TabsTrigger>
          <TabsTrigger value="photos">{t('drawer.tabs.photos')}</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className={scrollableClasses}>
          <OrderSummaryCard
            order={order}
            orderInfo={orderInfo}
            pricing={pricing}
            status={status}
            visibility={visibility}
            priceUi={priceUi}
            displayForUser={displayForUser}
            statusConfig={statusConfig}
            orderAgainConfig={orderAgainConfig}
            onEditClient={canManagePost ? () => setIsChangeClientOpen(true) : undefined}
          />
          <OrderTimeline order={order} displayForUser={displayForUser} />
        </TabsContent>

        <TabsContent value="details" className={scrollableClasses}>
          <OrderDetailsTab order={order} priceUi={priceUi} />
        </TabsContent>

        <TabsContent value="photos" className={scrollableClasses}>
          <OrderPhotosTab order={order} canAdd={canAddPhotos} />
        </TabsContent>
      </Tabs>

      <div className="flex-shrink-0 border-t bg-white p-3 pt-5">
        {CustomerOrderActions ? (
          <CustomerOrderActions order={order} displayForUser={displayForUser} />
        ) : (
          <OrderActions
            order={order}
            displayForUser={displayForUser}
            changeClientOpen={isChangeClientOpen}
            onChangeClientOpenChange={setIsChangeClientOpen}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Self-contained Order Drawer.
 *
 * Render ONE instance per page. Open from anywhere via:
 * ```ts
 * useOrderDrawerStore.getState().open(orderId)
 * ```
 */
export const OrderDrawer: React.FC<OrderDrawerProps> = ({
  displayForUser,
  orderAgainConfig,
  statusConfig,
  CustomerOrderActions,
  syncUrl,
}) => {
  const { t } = useTranslation('m-order');

  return (
    <StackedDrawer<Order>
      useStore={useOrderDrawerStore}
      useItemApi={id => useOrderApi(id)}
      syncUrl={syncUrl}
      defaultParamKey="id"
      sheetClassName="flex flex-col p-8 bg-white"
      title={t('drawer.a11y.title')}
      description={t('drawer.a11y.description')}
      renderContent={order => (
        <OrderDrawerContent
          order={order}
          displayForUser={displayForUser}
          orderAgainConfig={orderAgainConfig}
          statusConfig={statusConfig}
          CustomerOrderActions={CustomerOrderActions}
        />
      )}
    />
  );
};
